<template>
  <div
    :class="[
      {
        [$style.staticContainer]: mode === 'static',
        [$style.flatContainer]: mode === 'flat',
      },
    ]"
    data-testid="map-legend"
    @wheel.prevent="handleWheelEvent"
    @mouseenter="enableWheelPrevent"
    @mouseleave="disableWheelPrevent"
  >
    <p v-if="dataRange.min == 0 && dataRange.max == 0" :class="$style.noData">
      {{ t('map.insufficientData') }}
    </p>

    <div v-else :class="$style.contentWrapper">
      <span :class="$style.caption">{{ legendExplanation }}</span>
      <span :class="$style.minPrice">{{ numberFormatted(dataRange.min) }} CHF/m<sup>2</sup></span>
      <div v-if="colors.length > 0" :class="$style.colorsContainer">
        <div
          v-for="color in colors"
          :key="color"
          :class="[$style.colorBlock]"
          :style="{ '--background-color': color }"
          data-testId="color-block"
        />
      </div>
      <span :class="$style.maxPrice">{{ numberFormatted(dataRange.max) }} CHF/m<sup>2</sup></span>
    </div>
  </div>
</template>
<script setup lang="ts">
import { hgNumber, type HgSupportedLanguage } from '@smg-real-estate/domus';
import isNil from 'lodash.isnil';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { isPlatformIs24 } from '@/helpers/platform';
import { PropertyType } from '@/models/iazi/common/enums';
import { type PriceRange } from '@/models/iazi/queries/getPriceDistributionResponse';
import useMapStore from '@/stores/useMapStore';

interface Props {
  mode?: 'static' | 'flat';
}

withDefaults(defineProps<Props>(), {
  mode: 'static',
});

const { t, locale } = useI18n();
const mapStore = useMapStore();
const { range, legendColors, propertyType } = storeToRefs(mapStore);
const preventWheel = ref(false);

const numberFormatted = (value: number | null | undefined): string => {
  return isNil(value) ? '' : hgNumber(value as number, locale.value as HgSupportedLanguage);
};

const dataRange = computed<PriceRange>(() => {
  if (isPlatformIs24()) {
    return range.value ?? { max: 0, min: 0 };
  } else {
    return range.value
      ? {
          max: Math.round(range.value.max / 100) * 100,
          min: Math.round(range.value.min / 100) * 100,
        }
      : { max: 0, min: 0 };
  }
});

const colors = computed<Array<string>>(() => {
  return legendColors.value ?? [];
});
const handleWheelEvent = (event: WheelEvent) => {
  if (preventWheel.value && (event.ctrlKey || event.metaKey)) {
    event.preventDefault();
  }
};

const legendExplanation = computed<string>((): string => {
  const textkey =
    'legend.description.' +
    (propertyType.value === PropertyType.Apartment ? 'apartments' : 'houses');
  return t(textkey);
});

const enableWheelPrevent = () => (preventWheel.value = true);
const disableWheelPrevent = () => (preventWheel.value = false);

onMounted(() => {
  document.addEventListener('wheel', handleWheelEvent, { passive: false });
});

onUnmounted(() => {
  document.removeEventListener('wheel', handleWheelEvent);
});
</script>
<style lang="scss" module>
@use '@/styles/variables' as *;

$zoom-button-size: 39px;

.contentWrapper {
  display: flex;
  gap: $space-50;

  .caption {
    width: 100%;

    @include body-3-bold;
  }
}

.colorsContainer {
  display: flex;
  height: $space-75;
}

.colorBlock {
  flex: 1;
  background-color: var(--background-color);
}

.staticContainer {
  position: absolute;
  bottom: $space-250;
  left: $space-100;
  z-index: 1;
  width: calc(100% - $space-100 * 3 - $space-150 - $zoom-button-size);
  padding: $space-100;
  background: $color-white;
  border-radius: $border-radius;
  box-shadow: $elevation-low;

  @include grid-media-query(sm) {
    width: 284px;
  }

  .noData {
    padding-top: $space-75;
    padding-bottom: $space-50;
    margin: 0;
    text-align: center;

    @include body-3-bold($color-grey-100);
  }

  .contentWrapper {
    flex-wrap: wrap;

    .colorsContainer {
      order: 2;
      width: 100%;
    }

    .minPrice,
    .maxPrice {
      flex: 1;

      @include heading-5;
    }

    .maxPrice {
      text-align: right;
    }
  }
}

.flatContainer {
  display: flex;
  width: 100%;
  padding-top: $space-75;

  .noData {
    flex: 1;
    margin: 0;
    text-align: center;

    @include caption($color-grey-100);
  }

  .contentWrapper {
    flex-wrap: wrap;
    align-items: center;
    width: inherit;

    .colorsContainer {
      flex: 1;
    }

    .minPrice,
    .maxPrice {
      @include caption;
    }
  }
}
</style>
